import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Crumbs, readyPills } from "@/components/layout/Crumbs";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import { useMall } from "@/context/MallContext";
import { useShop } from "@/lib/useShop";
import { SellerListings } from "@/vault/marketplace/SellerListings";
import { useFeature } from "@/context/SiteModeContext";
import { cardTheme, receded, sortStoreFirst, themeOf } from "@/lib/mall";
import { PhotoCard, garmentCard } from "@/components/PhotoCard";
import { READY_BY_KEY, READY_IN } from "@/lib/nav";
import { SHIRT_KINDS, kindOf } from "@/lib/shirtKinds";
import { LandingHead } from "@/components/shop/GroupCard";

const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

/** One pill row of filters: a mono label, then the choices. */
function FilterRow({ label, choices, value, onPick, theme, testid }) {
    return (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={testid}>
            <span className="mr-1 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
            {choices.map((c) => {
                const on = (c.key || "all") === (value || "all");
                return (
                    <button key={c.key || "all"} type="button" onClick={() => onPick(c.key)} data-testid={`${testid}-${c.key || "all"}`} aria-pressed={on}
                        className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${on ? "border-transparent" : "border-zinc-300 text-zinc-800 hover:border-zinc-900"} ${c.count === 0 ? "opacity-40" : ""}`}
                        style={on ? { background: theme.primary, color: theme.on || "#fff" } : undefined}>
                        {c.hex && <span className="h-3 w-3 border border-black/20" style={{ background: c.hex }} />}
                        {c.label}
                    </button>
                );
            })}
        </div>
    );
}

/** `/shirts/:kind` — the garment grid for one shelf (white, colored, tanks, fleece…). A card opens the full-screen designer. */
export default function Shirts() {
    const listingsOn = useFeature("listings");
    const { kind: kindKey } = useParams();
    const kind = kindOf(kindKey);
    const [params, setParams] = useSearchParams();
    const { slug: activeSlug, store: activeStore } = useMall();
    const { shopSlug } = useShop();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const ready = READY_BY_KEY[params.get("ready")] ? params.get("ready") : null;
    const color = params.get("color") || null;
    const q = shopSlug ? `?store=${shopSlug}` : "";

    useEffect(() => {
        if (!shopSlug) return;
        setLoading(true);
        api.get("/catalog/shirts", { params: { store: shopSlug, ready: ready || undefined } })
            .then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false));
    }, [shopSlug, ready]);

    const setFilter = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value); else next.delete(key);
        setParams(next);
    };

    if (loading && !data) return <Layout><div className="flex min-h-[60vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div></Layout>;
    if (!data) return <Layout><div className="mx-auto max-w-3xl px-4 py-24 text-center"><h1 className="font-display text-3xl font-black">The shelf is empty</h1></div></Layout>;

    const theme = themeOf(activeStore);
    const readyItem = ready ? READY_BY_KEY[ready] : null;
    const all = sortStoreFirst(data.products, activeSlug).filter(kind.match);
    const colors = [{ key: null, label: "All" }, ...(data.colors || []).filter((c) => all.some((p) => slug(p.color_name) === slug(c.name))).map((c) => ({ key: slug(c.name), label: c.name, hex: c.hex }))];
    const shown = all.filter((p) => !color || slug(p.color_name) === color);
    const countBy = (k) => (k ? all.filter((p) => slug(p.color_name) === k).length : all.length);
    const siblings = SHIRT_KINDS.filter((k) => k.key !== kind.key && data.products.some(k.match));

    return (
        <Layout title="Shirts">
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="shirts-page" data-kind={kind.key}>
                <Reveal>
                    <Crumbs trail={[{ label: "Shirts", to: "/shirts" }, { label: kind.label }, ...(readyItem ? [{ label: readyItem.label }] : [])]}
                        pills={readyPills(`/shirts/${kind.key}`, data.counts, ready, READY_IN)} />
                    <div className="mt-4">
                        <LandingHead kicker={activeStore ? `Printed by ${activeStore.name}` : "Shirts"} testid="shirts-head"
                            title={<span data-testid="shirts-title">{kind.label}{readyItem ? <span className="text-zinc-400"> · {readyItem.label}</span> : ""}</span>}
                            line={<span data-testid="shirts-note">{shown.length} option{shown.length === 1 ? "" : "s"} · {readyItem ? readyItem.note : kind.line} Tap a garment to design it on screen.</span>} />
                    </div>
                    <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                        {colors.length > 2 && (
                            <FilterRow label="Color" theme={theme} value={color} onPick={(v) => setFilter("color", v)} testid="filter-color"
                                choices={colors.map((c) => ({ ...c, count: countBy(c.key) }))} />
                        )}
                        {siblings.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5" data-testid="kind-switch">
                                <span className="mr-1 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Shelf</span>
                                {siblings.map((k) => (
                                    <Link key={k.key} to={`/shirts/${k.key}${q}`} data-testid={`kind-switch-${k.key}`}
                                        className="inline-flex items-center border border-zinc-300 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-zinc-800 hover:border-zinc-900">{k.label}</Link>
                                ))}
                            </div>
                        )}
                    </div>
                </Reveal>

                <div className="mt-6 flex items-baseline justify-between">
                    <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500" data-testid="shirts-grid-heading">
                        {kind.label} · {shown.length}
                    </h2>
                    {color && (
                        <button type="button" onClick={() => setParams(ready ? { ready } : {})} className="text-xs font-semibold underline" data-testid="filter-clear">Clear filters</button>
                    )}
                </div>
                {shown.length ? (
                    <div className="mt-3 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="shirts-grid">
                        {shown.map((p, i) => (
                            <Reveal key={p.slug} delay={Math.min(i, 8) * 0.04}>
                                <PhotoCard {...garmentCard(p, { name: p.group_label || p.kind_name }, activeStore, activeSlug, cardTheme)} receded={receded(p, activeSlug)} />
                            </Reveal>
                        ))}
                    </div>
                ) : (
                    <div className="mt-3 border-2 border-dashed border-zinc-300 bg-white p-10 text-center" data-testid="shirts-empty">
                        <p className="text-base font-semibold text-zinc-900">Nothing on this shelf with those filters.</p>
                        <Link to={`/shirts${q}`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-zinc-900 underline">Back to every shelf <ArrowRight size={14} /></Link>
                    </div>
                )}
            </section>

            {listingsOn && <SellerListings kind="apparel" group="white" heading="Also for sale from other sellers"
                blurb="Classified ads for blanks and finished apparel, posted by other shops. You deal with the seller directly."
                sellCta="Sell your apparel" testid="seller-apparel-shirts" />}
        </Layout>
    );
}
